import i18n from '@/i18n';
import { normalizeWorkspaceMode } from './accountCenterUtils';
import {
  getImageSpaceSourceMeta,
  resolveImageSpaceSourceKey,
} from '@/pages/ipe/workbench/constants/imageSpaceSourceOptions';
import { isImageSpaceCollectionSource } from './imageSpaceStorageStats';
import { userLocal } from '@/utils/ipeUserLocalState';

const FOLDERS_STORAGE_KEY = 'ipe-image-space-folders-v1';
const FOLDERS_TEAM_KEY_PREFIX = `${FOLDERS_STORAGE_KEY}-team-`;

export const IMAGE_SPACE_TRASH_RETENTION_DAYS = 7;
export const IMAGE_SPACE_TRASH_RETENTION_MS = IMAGE_SPACE_TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;

export const IMAGE_SPACE_CONTENT_TABS = [
  { id: 'favorites', label: '我的收藏' },
  { id: 'trash', label: '回收站' },
];

export function resolveImageSpaceContentTab(navId) {
  if (navId === 'spaceFavorites') return 'favorites';
  if (navId === 'spaceTrash') return 'trash';
  if (navId === 'spaceCollection') return 'all';
  return 'all';
}

function normalizeFolderId(value) {
  return value == null || value === '' ? '' : String(value).trim();
}

function normalizeFolder(item) {
  if (!item || !item.id) return null;
  const name = String(item.name || '').trim();
  if (!name) return null;
  return {
    id: normalizeFolderId(item.id),
    name,
    parentId: normalizeFolderId(item.parentId),
    scope: normalizeImageSpaceFolderScope(item.scope),
    teamShared: item.teamShared === true || String(item.teamShared) === 'true',
    ownerId: item.ownerId != null && item.ownerId !== '' ? String(item.ownerId) : '',
    ownerUserId: item.ownerUserId != null && item.ownerUserId !== '' ? String(item.ownerUserId) : '',
    teamId: item.teamId != null && item.teamId !== '' ? String(item.teamId) : '',
    createdAt: item.createdAt || new Date().toISOString(),
    deletedAt: item.deletedAt || null,
  };
}

function groupActiveFoldersByParent(folders) {
  const grouped = new Map();
  (Array.isArray(folders) ? folders : [])
    .filter((item) => item && !isImageSpaceFolderDeleted(item))
    .forEach((item) => {
      const parentId = normalizeFolderId(item.parentId);
      const bucket = grouped.get(parentId) || [];
      bucket.push(item);
      grouped.set(parentId, bucket);
    });
  return grouped;
}

export function isImageSpaceFolderDeleted(folder) {
  return Boolean(folder?.deletedAt);
}

export function getTrashPurgeTime(deletedAt) {
  const timestamp = new Date(deletedAt).getTime();
  if (Number.isNaN(timestamp)) return 0;
  return timestamp + IMAGE_SPACE_TRASH_RETENTION_MS;
}

export function isTrashExpired(deletedAt) {
  if (!deletedAt) return false;
  return Date.now() >= getTrashPurgeTime(deletedAt);
}

export function getTrashRemainingDays(deletedAt) {
  if (!deletedAt) return IMAGE_SPACE_TRASH_RETENTION_DAYS;
  const remaining = getTrashPurgeTime(deletedAt) - Date.now();
  if (remaining <= 0) return 0;
  return Math.ceil(remaining / (24 * 60 * 60 * 1000));
}

export function formatTrashRemainingLabel(deletedAt) {
  const days = getTrashRemainingDays(deletedAt);
  if (days <= 0) return i18n.t('workbench:spaceBoard.trashPermanentSoon');
  return i18n.t('workbench:spaceBoard.trashDaysLeft', { days });
}

export function sortByTrashRemainingDays(items) {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const daysA = getTrashRemainingDays(a.deletedAt);
    const daysB = getTrashRemainingDays(b.deletedAt);
    if (daysA !== daysB) return daysA - daysB;
    const timeA = new Date(a.deletedAt || 0).getTime();
    const timeB = new Date(b.deletedAt || 0).getTime();
    return timeA - timeB;
  });
}

export function isImageSpaceFolderShared(folder) {
  return folder?.teamShared === true || String(folder?.teamShared) === 'true';
}

export function canReadImageSpaceFolder(folder, {
  authUserId = '',
  permissionScope = 'self',
} = {}) {
  if (!folder) return false;
  if (permissionScope === 'team_all') return true;
  const userId = authUserId != null && authUserId !== '' ? String(authUserId) : '';
  const ownerId = String(folder.ownerId || folder.ownerUserId || '');
  if (userId && ownerId && ownerId === userId) return true;
  return isImageSpaceFolderShared(folder);
}

export function filterReadableImageSpaceFolders(folders, access = {}) {
  return (Array.isArray(folders) ? folders : []).filter((folder) => canReadImageSpaceFolder(folder, access));
}

function teamFoldersCacheKey(teamId) {
  return `${FOLDERS_STORAGE_KEY}-team-${String(teamId || 'unknown')}`;
}

export function loadTeamImageSpaceFolderCache(teamId) {
  try {
    const parsed = userLocal.getJson(teamFoldersCacheKey(teamId), null);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeFolder).filter(Boolean);
  } catch {
    return [];
  }
}

/** 按当前工作区读取文件夹：团队走 team 缓存，个人走 localStorage（绝不混用） */
export function loadWorkspaceImageSpaceFolders({ teamMode = false, teamId = '' } = {}) {
  if (teamMode) {
    return teamId ? loadTeamImageSpaceFolderCache(teamId) : [];
  }
  return loadImageSpaceFolders();
}

export function saveTeamImageSpaceFolderCache(folders, teamId) {
  try {
    const normalized = (folders || []).map(normalizeFolder).filter(Boolean);
    userLocal.setJson(teamFoldersCacheKey(teamId), normalized);
    return normalized;
  } catch {
    return folders || [];
  }
}

export function loadImageSpaceFolders() {
  try {
    const parsed = userLocal.getJson(FOLDERS_STORAGE_KEY, null);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeFolder).filter(Boolean);
  } catch {
    return [];
  }
}

export function saveImageSpaceFolders(folders) {
  try {
    const normalized = (folders || []).map(normalizeFolder).filter(Boolean);
    userLocal.setJson(FOLDERS_STORAGE_KEY, normalized);
    return normalized;
  } catch {
    return folders || [];
  }
}

export function clearImageSpaceFoldersForLogout() {
  userLocal.dropLegacyKeys(FOLDERS_STORAGE_KEY);
  userLocal.dropLegacyKeyPrefix(FOLDERS_TEAM_KEY_PREFIX);
}

export function filterImageSpaceFoldersByParent(folders, parentId = '') {
  const list = Array.isArray(folders) ? folders : [];
  const normalizedParentId = normalizeFolderId(parentId);
  return list.filter((item) => normalizeFolderId(item.parentId) === normalizedParentId);
}

export function filterActiveImageSpaceFoldersByParent(folders, parentId = '') {
  const list = Array.isArray(folders) ? folders : [];
  return filterImageSpaceFoldersByParent(
    list.filter((item) => !isImageSpaceFolderDeleted(item)),
    parentId,
  );
}

export function filterTrashedImageSpaceFoldersByParent(folders, parentId = '') {
  const list = Array.isArray(folders) ? folders : [];
  const normalizedParentId = parentId ? String(parentId) : '';

  return list.filter((item) => {
    if (!isImageSpaceFolderDeleted(item)) return false;
    if (String(item.parentId || '') === normalizedParentId) return true;
    if (!normalizedParentId && item.parentId) {
      const parent = list.find((folder) => folder.id === item.parentId);
      return Boolean(parent && !isImageSpaceFolderDeleted(parent));
    }
    return false;
  });
}

export function buildImageSpaceFolderPath(folders, folderId) {
  if (!folderId) return [];
  const list = Array.isArray(folders) ? folders : [];
  const map = new Map(list.map((item) => [item.id, item]));
  const path = [];
  let current = map.get(String(folderId));
  const guard = new Set();

  while (current && !guard.has(current.id)) {
    guard.add(current.id);
    path.unshift(current);
    current = current.parentId ? map.get(String(current.parentId)) : null;
  }

  return path;
}

export function collectImageSpaceFolderDescendantIds(folders, folderId) {
  const list = Array.isArray(folders) ? folders : [];
  const rootId = normalizeFolderId(folderId);
  if (!rootId) return new Set();

  const ids = new Set([rootId]);
  let changed = true;

  while (changed) {
    changed = false;
    // 改用 for...of，完全消除了循环内的函数声明
    for (const item of list) {
      const itemId = normalizeFolderId(item.id);
      const parentId = normalizeFolderId(item.parentId);
      if (parentId && ids.has(parentId) && !ids.has(itemId)) {
        ids.add(itemId);
        changed = true;
      }
    }
  }

  return ids;
}

function createFolderId(seed = Date.now()) {
  return `folder-${seed}-${Math.random().toString(36).slice(2, 8)}`;
}

const IMAGE_SPACE_FOLDER_NAME_MAX_LEN = 40;
export const IMAGE_SPACE_FOLDER_DUPLICATE_NAME_MESSAGE = '同级已有同名文件夹，请使用其他名称';
export const IMAGE_SPACE_FOLDER_DEFAULT_SCOPE = 'material';

export function normalizeImageSpaceFolderScope(scope) {
  return scope === 'collection' ? 'collection' : IMAGE_SPACE_FOLDER_DEFAULT_SCOPE;
}

export function resolveImageSpaceFolderScope(folder) {
  return normalizeImageSpaceFolderScope(folder?.scope);
}

export function resolveImageSpaceFolderScopeForImage(image) {
  return isImageSpaceCollectionSource(image) ? 'collection' : IMAGE_SPACE_FOLDER_DEFAULT_SCOPE;
}

export function resolveImageSpaceFolderCreateScope(folders, parentId, preferredScope = IMAGE_SPACE_FOLDER_DEFAULT_SCOPE) {
  const normalizedParentId = normalizeFolderId(parentId);
  if (normalizedParentId) {
    const parent = (Array.isArray(folders) ? folders : []).find(
      (item) => normalizeFolderId(item.id) === normalizedParentId,
    );
    if (parent) return resolveImageSpaceFolderScope(parent);
  }
  return normalizeImageSpaceFolderScope(preferredScope);
}

export function filterFoldersForSourceCategory(folders, parentId, category) {
  const list = Array.isArray(folders) ? folders : [];
  const children = filterImageSpaceFoldersByParent(
    list.filter((item) => !isImageSpaceFolderDeleted(item)),
    parentId,
  );
  if (category === 'all') return children;
  const scope = category === 'collection' ? 'collection' : IMAGE_SPACE_FOLDER_DEFAULT_SCOPE;
  return children.filter((item) => resolveImageSpaceFolderScope(item) === scope);
}

export function isImageSpaceFolderNameTaken(
  folders,
  name,
  parentId = '',
  excludeFolderId = '',
  scope = IMAGE_SPACE_FOLDER_DEFAULT_SCOPE,
) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return false;
  const normalizedParentId = normalizeFolderId(parentId);
  const excludeId = normalizeFolderId(excludeFolderId);
  const normalizedScope = normalizeImageSpaceFolderScope(scope);
  return (Array.isArray(folders) ? folders : []).some((item) => {
    if (!item || isImageSpaceFolderDeleted(item)) return false;
    if (excludeId && normalizeFolderId(item.id) === excludeId) return false;
    if (normalizeFolderId(item.parentId) !== normalizedParentId) return false;
    if (resolveImageSpaceFolderScope(item) !== normalizedScope) return false;
    return String(item.name || '').trim() === trimmed;
  });
}

function buildUniqueImageSpaceFolderName(
  folders,
  desiredName,
  parentId = '',
  excludeFolderId = '',
  scope = IMAGE_SPACE_FOLDER_DEFAULT_SCOPE,
) {
  const trimmed = String(desiredName || '').trim();
  if (!trimmed) return trimmed;
  if (!isImageSpaceFolderNameTaken(folders, trimmed, parentId, excludeFolderId, scope)) {
    return trimmed;
  }

  let counter = 2;
  while (counter < 10000) {
    const suffix = ` (${counter})`;
    const baseMaxLen = IMAGE_SPACE_FOLDER_NAME_MAX_LEN - suffix.length;
    const base = trimmed.length > baseMaxLen ? trimmed.slice(0, baseMaxLen) : trimmed;
    const candidate = `${base}${suffix}`;
    if (!isImageSpaceFolderNameTaken(folders, candidate, parentId, excludeFolderId, scope)) {
      return candidate;
    }
    counter += 1;
  }

  return `${trimmed.slice(0, IMAGE_SPACE_FOLDER_NAME_MAX_LEN - 4)}_${Date.now() % 10000}`;
}

/** 构建新文件夹对象（不写入任何存储，供团队/个人各自持久化） */
export function buildNewImageSpaceFolder(
  name,
  parentId = '',
  preferredScope = IMAGE_SPACE_FOLDER_DEFAULT_SCOPE,
  folders = null,
) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return null;
  const list = Array.isArray(folders) ? folders : loadImageSpaceFolders();
  const normalizedParentId = normalizeFolderId(parentId);
  const scope = resolveImageSpaceFolderCreateScope(list, normalizedParentId, preferredScope);
  if (isImageSpaceFolderNameTaken(list, trimmed, normalizedParentId, '', scope)) {
    return null;
  }
  return {
    id: createFolderId(),
    name: trimmed,
    parentId: normalizedParentId,
    scope,
    createdAt: new Date().toISOString(),
  };
}

export function createImageSpaceFolder(name, parentId = '', preferredScope = IMAGE_SPACE_FOLDER_DEFAULT_SCOPE) {
  const folders = loadImageSpaceFolders();
  const folder = buildNewImageSpaceFolder(name, parentId, preferredScope, folders);
  if (!folder) return null;
  saveImageSpaceFolders([...folders, folder]);
  return folder;
}

export function renameImageSpaceFolder(folderId, name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return null;
  const folders = loadImageSpaceFolders();
  const targetId = String(folderId || '');
  const current = folders.find((item) => item.id === targetId);
  if (!current) return null;
  if (isImageSpaceFolderNameTaken(
    folders,
    trimmed,
    current.parentId,
    targetId,
    resolveImageSpaceFolderScope(current),
  )) {
    return null;
  }
  const next = folders.map((item) => (
    item.id === targetId ? { ...item, name: trimmed } : item
  ));
  saveImageSpaceFolders(next);
  return next.find((item) => item.id === targetId) || null;
}

export function buildImageSpaceFolderCopyName(folderName, folders = null, parentId = '', scope = IMAGE_SPACE_FOLDER_DEFAULT_SCOPE) {
  const base = String(folderName || '').trim() || i18n.t('workbench:spaceBoard.unnamedFolder');
  if (!folders) return `${base}${i18n.t('workbench:spaceBoard.copyNameSuffix')}`;

  const normalizedParentId = normalizeFolderId(parentId);
  const normalizedScope = normalizeImageSpaceFolderScope(scope);
  let counter = 1;
  while (counter < 10000) {
    const suffix = counter === 1 ? i18n.t('workbench:spaceBoard.copyNameSuffix') : `${i18n.t('workbench:spaceBoard.copyNameSuffix')}${counter}`;
    const baseMaxLen = IMAGE_SPACE_FOLDER_NAME_MAX_LEN - suffix.length;
    const truncatedBase = base.length > baseMaxLen ? base.slice(0, baseMaxLen) : base;
    const candidate = `${truncatedBase}${suffix}`;
    if (!isImageSpaceFolderNameTaken(folders, candidate, normalizedParentId, '', normalizedScope)) {
      return candidate;
    }
    counter += 1;
  }

  return buildUniqueImageSpaceFolderName(
    folders,
    `${base}${i18n.t('workbench:spaceBoard.copyNameSuffix')}`,
    normalizedParentId,
    '',
    normalizedScope,
  );
}

export function copyImageSpaceFolder(folderId, copyName) {
  const folders = loadImageSpaceFolders();
  const sourceId = String(folderId || '');
  const source = folders.find((item) => item.id === sourceId);
  if (!source) return null;

  const rootName = String(copyName || '').trim() || buildImageSpaceFolderCopyName(
    source.name,
    folders,
    source.parentId,
    resolveImageSpaceFolderScope(source),
  );
  if (isImageSpaceFolderNameTaken(
    folders,
    rootName,
    source.parentId,
    '',
    resolveImageSpaceFolderScope(source),
  )) {
    return null;
  }
  const subtreeIds = collectImageSpaceFolderDescendantIds(folders, sourceId);
  const subtree = folders.filter((item) => subtreeIds.has(item.id));
  const idMap = new Map();
  let seed = Date.now();

  subtree.forEach((item) => {
    idMap.set(item.id, createFolderId(seed));
    seed += 1;
  });

  const copied = subtree
    .filter((item) => !isImageSpaceFolderDeleted(item))
    .map((item) => ({
      ...item,
      id: idMap.get(item.id),
      parentId: item.id === sourceId
        ? String(source.parentId || '')
        : String(idMap.get(item.parentId) || ''),
      name: item.id === sourceId ? rootName : item.name,
      createdAt: new Date().toISOString(),
      deletedAt: null,
    }));

  saveImageSpaceFolders([...folders, ...copied]);
  return copied.find((item) => item.id === idMap.get(sourceId)) || copied[0] || null;
}

export function moveImageSpaceFolder(folderId, newParentId = '') {
  const folders = loadImageSpaceFolders();
  const targetId = String(folderId || '');
  const nextParentId = newParentId ? String(newParentId) : '';
  const descendants = collectImageSpaceFolderDescendantIds(folders, targetId);

  const movingFolder = folders.find((item) => item.id === targetId);
  if (!movingFolder) return false;
  if (nextParentId && descendants.has(nextParentId)) return false;
  const movingScope = resolveImageSpaceFolderScope(movingFolder);
  if (nextParentId) {
    const newParent = folders.find((item) => item.id === nextParentId);
    if (!newParent || resolveImageSpaceFolderScope(newParent) !== movingScope) return false;
  }
  if (isImageSpaceFolderNameTaken(
    folders,
    movingFolder.name,
    nextParentId,
    targetId,
    movingScope,
  )) {
    return false;
  }

  const next = folders.map((item) => (
    item.id === targetId ? { ...item, parentId: nextParentId } : item
  ));
  saveImageSpaceFolders(next);
  return true;
}

export function removeImageSpaceFolders(folderIds) {
  const removeSet = folderIds instanceof Set ? folderIds : new Set(folderIds);
  const folders = loadImageSpaceFolders();
  const next = folders.filter((item) => !removeSet.has(item.id));
  saveImageSpaceFolders(next);
  return next;
}

export function trashImageSpaceFolders(folderIds, deletedAt = new Date().toISOString()) {
  const trashSet = folderIds instanceof Set ? folderIds : new Set(folderIds);
  if (!trashSet.size) return loadImageSpaceFolders();
  const folders = loadImageSpaceFolders();
  const next = folders.map((item) => (
    trashSet.has(item.id) ? { ...item, deletedAt } : item
  ));
  saveImageSpaceFolders(next);
  return next;
}

export function restoreImageSpaceFolders(folderIds) {
  const restoreSet = folderIds instanceof Set ? folderIds : new Set(folderIds);
  if (!restoreSet.size) return loadImageSpaceFolders();
  const folders = loadImageSpaceFolders();
  const next = folders.map((item) => (
    restoreSet.has(item.id) ? { ...item, deletedAt: null } : item
  ));
  saveImageSpaceFolders(next);
  return next;
}

export function purgeExpiredTrashedFolders() {
  const folders = loadImageSpaceFolders();
  const removedIds = folders
    .filter((item) => isImageSpaceFolderDeleted(item) && isTrashExpired(item.deletedAt))
    .map((item) => item.id);
  if (!removedIds.length) return [];
  removeImageSpaceFolders(removedIds);
  return removedIds;
}

export function buildImageSpaceFolderMoveTree(folders, folderId, { scope } = {}) {
  let list = (Array.isArray(folders) ? folders : [])
    .map(normalizeFolder)
    .filter(Boolean);
  if (scope) {
    const normalizedScope = normalizeImageSpaceFolderScope(scope);
    list = list.filter((item) => resolveImageSpaceFolderScope(item) === normalizedScope);
  }
  const excludeIds = collectImageSpaceFolderDescendantIds(list, folderId);
  const childrenByParent = groupActiveFoldersByParent(list);

  const buildNodes = (parentId, depth) => {
    const normalizedParentId = normalizeFolderId(parentId);
    return (childrenByParent.get(normalizedParentId) || [])
      .filter((item) => !excludeIds.has(normalizeFolderId(item.id)))
      .map((item) => ({
        id: item.id,
        name: item.name,
        depth,
        children: buildNodes(item.id, depth + 1),
      }));
  };

  return {
    id: '',
    name: '全部图片',
    depth: 0,
    children: buildNodes('', 1),
  };
}

/** @deprecated 使用 buildImageSpaceFolderMoveTree */
export function buildImageSpaceFolderMoveOptions(folders, folderId) {
  const tree = buildImageSpaceFolderMoveTree(folders, folderId);
  const flatten = (node) => [
    { id: node.id, name: node.name, depth: node.depth },
    ...(node.children || []).flatMap(flatten),
  ];
  return flatten(tree);
}

export function collectImageIdsInFolders(images, folderIds) {
  const idSet = folderIds instanceof Set ? folderIds : new Set(folderIds);
  return (Array.isArray(images) ? images : [])
    .filter((item) => idSet.has(String(item.folderId || '')))
    .map((item) => item.id);
}

export function collectImageSpaceFolderSubtreeIds(folders, folderIds) {
  const roots = Array.isArray(folderIds) ? folderIds : [...folderIds];
  const allIds = new Set();
  roots.forEach((folderId) => {
    collectImageSpaceFolderDescendantIds(folders, folderId).forEach((id) => allIds.add(id));
  });
  return allIds;
}

/** 将选中文件夹（含子级）与图片合并为一次移入回收站的清单（仅未删除项） */
export function summarizeActiveFolderTrash(folders, images, { folderIds = [], imageIds = [] } = {}) {
  const folderRootIds = Array.isArray(folderIds) ? folderIds : [];
  const directImageIds = Array.isArray(imageIds) ? imageIds.filter(Boolean) : [];
  const subtreeIds = collectImageSpaceFolderSubtreeIds(folders, folderRootIds);
  const nestedImageIds = collectImageIdsInFolders(
    (Array.isArray(images) ? images : []).filter((item) => !isImageSpaceDeleted(item)),
    subtreeIds,
  );
  const mergedImageIds = [...new Set([...directImageIds, ...nestedImageIds])];
  return {
    folderCount: subtreeIds.size,
    imageCount: mergedImageIds.length,
    folderIds: [...subtreeIds],
    imageIds: mergedImageIds,
  };
}

export function summarizeTrashPermanentDelete(folders, images, { folderIds = [], imageIds = [] } = {}) {
  const folderRootIds = Array.isArray(folderIds) ? folderIds : [];
  const directImageIds = Array.isArray(imageIds) ? imageIds : [];
  const subtreeIds = collectImageSpaceFolderSubtreeIds(folders, folderRootIds);
  const nestedImageIds = collectImageIdsInFolders(
    (Array.isArray(images) ? images : []).filter((item) => isImageSpaceDeleted(item)),
    subtreeIds,
  );
  const mergedImageIds = [...new Set([...directImageIds, ...nestedImageIds])];
  return {
    folderCount: subtreeIds.size,
    imageCount: mergedImageIds.length,
    folderIds: [...subtreeIds],
    imageIds: mergedImageIds,
  };
}

/** 删除确认弹窗展示文案（打开弹窗时预计算，避免大列表重复遍历） */
export function buildImageSpaceDeleteTargetLabel({
  folderIds = [],
  imageIds = [],
  folders = [],
  summary = null,
} = {}) {
  const folderRootCount = folderIds.length;
  const directImageCount = imageIds.length;
  if (folderRootCount === 1 && directImageCount === 0) {
    const folder = (Array.isArray(folders) ? folders : []).find((item) => item.id === folderIds[0]);
    return i18n.t('workbench:spaceBoard.deleteFolderNamed', {
      name: folder?.name || i18n.t('workbench:spaceBoard.unnamedFolder'),
    });
  }
  if (folderRootCount > 0 && directImageCount > 0) {
    return i18n.t('workbench:spaceBoard.deleteFoldersAndImages', {
      folders: folderRootCount,
      images: directImageCount,
    });
  }
  if (folderRootCount > 0) {
    return folderRootCount > 1
      ? i18n.t('workbench:spaceBoard.deleteFolders', { count: folderRootCount })
      : i18n.t('workbench:spaceBoard.deleteThisFolder');
  }
  const effectiveCount = summary?.imageCount ?? directImageCount;
  if (effectiveCount > 1) {
    return i18n.t('workbench:spaceBoard.deleteImages', { count: effectiveCount });
  }
  return i18n.t('workbench:spaceBoard.deleteThisImage');
}

export function isImageSpaceFavorite(image) {
  return image?.favorite === true || image?.isFavorite === true;
}

export function isImageSpaceDeleted(image) {
  if (!image || typeof image !== 'object') return false;
  const deletedAt = image.deletedAt;
  if (deletedAt == null) return false;
  const text = String(deletedAt).trim();
  // 与后端 ImageSpaceDedupeService.isActive / 前端 isImageSpaceListActiveRow 对齐
  if (!text || text === 'null' || text === 'undefined') return false;
  return true;
}

export function filterImageSpaceBySourceCategory(images, category) {
  const list = Array.isArray(images) ? images : [];
  const active = list.filter((item) => !isImageSpaceDeleted(item));
  if (category === 'collection') {
    return active.filter((item) => isImageSpaceCollectionSource(item));
  }
  if (category === 'material') {
    return active.filter((item) => !isImageSpaceCollectionSource(item));
  }
  return active;
}

export function filterImageSpaceByContentTab(images, tabId) {
  const list = Array.isArray(images) ? images : [];
  if (tabId === 'favorites') {
    return list.filter((item) => isImageSpaceFavorite(item) && !isImageSpaceDeleted(item));
  }
  if (tabId === 'trash') {
    return list.filter((item) => isImageSpaceDeleted(item));
  }
  if (tabId === 'collection') {
    return list.filter((item) => !isImageSpaceDeleted(item) && isImageSpaceCollectionSource(item));
  }
  return list.filter((item) => !isImageSpaceDeleted(item));
}

export function filterImageSpaceByFolder(images, folderId) {
  const list = Array.isArray(images) ? images : [];
  const normalizedFolderId = folderId ? String(folderId) : '';
  return list.filter((item) => String(item.folderId || '') === normalizedFolderId);
}

/**
 * 按来源分类与当前文件夹过滤图片：采集/素材在根级展示该分类下全部图片（不限 folderId 为空）。
 * @param {Array<Record<string, unknown>>} images
 * @param {string} folderId
 * @param {'all' | 'material' | 'collection'} sourceCategory
 * @returns {Array<Record<string, unknown>>}
 */
export function filterImageSpaceImagesByFolderContext(images, folderId, sourceCategory) {
  const list = Array.isArray(images) ? images : [];
  const normalizedFolderId = folderId ? String(folderId) : '';
  if (sourceCategory === 'collection' || sourceCategory === 'material') {
    if (!normalizedFolderId) {
      const scope = sourceCategory === 'collection' ? 'collection' : IMAGE_SPACE_FOLDER_DEFAULT_SCOPE;
      return list.filter((item) => resolveImageSpaceFolderScopeForImage(item) === scope);
    }
    return filterImageSpaceByFolder(list, normalizedFolderId);
  }
  return filterImageSpaceByFolder(list, normalizedFolderId);
}

/**
 * 解析图片归属工作区。无标记的历史数据默认 personal，确保个人上传不会进入团队视图。
 */
export function resolveImageRecordWorkspace(image) {
  const raw = String(image?.workspaceMode || image?.captureSpace || '').trim().toLowerCase();
  if (raw === 'team' || raw === 'personal') {
    return raw;
  }
  if (resolveImageSpaceSourceKey(image) === 'materialCrawlerUser') {
    return 'team';
  }
  return 'personal';
}

/**
 * 按工作台个人/团队模式过滤图片（插件采集写入 workspaceMode / captureSpace）。
 */
export function filterImageSpaceByWorkspaceMode(images, workspaceMode) {
  const mode = normalizeWorkspaceMode(workspaceMode);
  return (Array.isArray(images) ? images : []).filter((image) => (
    resolveImageRecordWorkspace(image) === mode
  ));
}

export function stampImageSpaceWorkspaceFields(image, workspaceMode, teamId = null) {
  if (!image || typeof image !== 'object') return image;
  const mode = normalizeWorkspaceMode(workspaceMode);
  const next = { ...image, workspaceMode: mode, captureSpace: mode };
  if (mode === 'team' && teamId != null && teamId !== '') {
    next.teamId = teamId;
  }
  return next;
}

/** 我的收藏 Tab 内三类来源筛选 */
export const IMAGE_SPACE_FAVORITE_CATEGORIES = [
  { id: 'official', label: '官方素材' },
  { id: 'space', label: '图片空间' },
  { id: 'collection', label: '采集图片' },
];

/**
 * 解析收藏图片在「我的收藏」中的分类（官方 / 图片空间 / 采集）。
 *
 * @param {Record<string, unknown> | null | undefined} image
 * @returns {'official' | 'space' | 'collection'}
 */
export function resolveImageSpaceFavoriteCategory(image) {
  // 1. 采集来源优先（含插件、用户、定时采集）
  if (isImageSpaceCollectionSource(image)) {
    return 'collection';
  }
  // 2. 素材库 / 官方素材中心入库
  const meta = getImageSpaceSourceMeta(image);
  if (meta.group === 'library') {
    return 'official';
  }
  // 3. 其余视为用户图片空间自有数据
  return 'space';
}

/**
 * 按收藏分类过滤图片列表。
 *
 * @param {Array<Record<string, unknown>>} images
 * @param {'official' | 'space' | 'collection'} categoryId
 * @returns {Array<Record<string, unknown>>}
 */
export function filterImageSpaceByFavoriteCategory(images, categoryId) {
  const list = Array.isArray(images) ? images : [];
  const cat = String(categoryId || '').trim();
  if (!cat) return list;
  return list.filter((item) => resolveImageSpaceFavoriteCategory(item) === cat);
}
